import React, { useState } from 'react'
import { FormItem, FormItemName } from 'uiComponents/form/formElements'
import TextInputFieldFormik from 'uiComponents/input/textInput/textInputFieldFormik'
import { isValidEmail } from 'utils/formFieldChecks'
import classNames from 'classnames'
import { Section } from '../components/section'
import { AppServices } from 'middleware/services'
import { showErrorMessage } from 'uiComponents/message'
import './personalInformation.scss'

const EmailConfirmationStatus = ({ confirmed }: { confirmed: boolean }) => (
    <div className={classNames('email-confirmation-status', { confirmed })}>
        <span>{confirmed ? 'Confirmed' : 'Not confirmed'}</span>
    </div>
)

interface PersonalInformationSectionProps {
    email: string
    emailConfirmed: boolean
    isNewUser: boolean
    isControlPanelLoginGranted: boolean
    checkEmailValid: (email: string) => Promise<boolean>
    setEmailChecked: (emailChecked: boolean) => void
}

export const PersonalInformationSection = ({
    email,
    emailConfirmed,
    isNewUser,
    isControlPanelLoginGranted,
    checkEmailValid,
    setEmailChecked,
}: PersonalInformationSectionProps) => {
    const [checkingEmail, setCheckingEmail] = useState(false)
    const [lastEmail, setLastEmail] = useState('')

    const checkEmail = async () => {
        if (isValidEmail(email) && email !== lastEmail) {
            setEmailChecked(false)
            setCheckingEmail(true)
            setLastEmail(email)

            const emailValid = await checkEmailValid(email)
            setEmailChecked(emailValid)
            setCheckingEmail(false)
        }
    }

    const [emailResent, setEmailResent] = useState(false)
    const [sendingEmail, setSendingEmail] = useState(false)
    const accountSettingsService = AppServices.accountSettingsService
    const resendEmailConfirmation = async () => {
        if (sendingEmail || emailResent) {
            return
        }
        try {
            setSendingEmail(true)
            await accountSettingsService.resendConfirmEmail(email)
            setEmailResent(true)
            setSendingEmail(false)
        } catch {
            showErrorMessage(
                'Could not resend confirmation email. Please try again later.',
                'resend_confirmation_email_error',
            )
            setSendingEmail(false)
        }
    }

    let resendEmailConfirmationText = 'Resend invite'
    if (sendingEmail) {
        resendEmailConfirmationText = 'Sending...'
    }
    if (emailResent) {
        resendEmailConfirmationText = 'Invite resent'
    }

    return (
        <Section title="Personal information" className="personal-information">
            <div className="row">
                <FormItem className="email">
                    <FormItemName>Email address</FormItemName>
                    <TextInputFieldFormik
                        name="email"
                        className={classNames({ loading: checkingEmail })}
                        disabled={!isNewUser || checkingEmail}
                        onBlur={checkEmail}
                        autoComplete="off"
                    />
                    <div
                        className={classNames('email-confirmation', {
                            visible: !isNewUser && isControlPanelLoginGranted,
                        })}
                    >
                        {!emailConfirmed ? (
                            <div
                                className={classNames('resend-email-confirmation', {
                                    disabled: sendingEmail || emailResent,
                                })}
                                onClick={resendEmailConfirmation}
                            >
                                {resendEmailConfirmationText}
                            </div>
                        ) : (
                            <div />
                        )}
                        <EmailConfirmationStatus confirmed={emailConfirmed} />
                    </div>
                </FormItem>
                <FormItem className="first-name">
                    <FormItemName>First name</FormItemName>
                    <TextInputFieldFormik name="firstName" autoComplete="off" />
                </FormItem>
                <FormItem className="last-name">
                    <FormItemName>Last name</FormItemName>
                    <TextInputFieldFormik name="lastName" autoComplete="off" />
                </FormItem>
            </div>
        </Section>
    )
}
